<template>
    <div>
        <div class="flex justify-between flex-col-reverse sm:flex-row sm:items-center">
            <div class="flex sm:items-center flex-col sm:flex-row">
                <table class="mx-2">
                    <tr>
                        <th scope="row" class="text-right text-sm whitespace-nowrap">{{ $t('commons.version') }} :</th>
                        <td class="px-3">
                            <app-select
                                :label="$t('commons.version')"
                                :value="invoice.id"
                                class=""
                                :show-label="false"
                                :disabled="!invoice.id"
                                @change="onVersionSelected($event)"
                            >
                                <option
                                    :value="version.id"
                                    v-for="(version, index) in invoice.versions"
                                    :key="version.id"
                                    :selected="selectedVersion.id === version.id"
                                >
                                    {{ invoice.versions.length - index }} ({{ version.createdAt | humanizeDate }})
                                </option>
                            </app-select>
                        </td>
                    </tr>
                </table>

                <div class="m-2 sm:my-0">
                    <app-button @click="onNewVersion" :disabled="!invoice.id">
                        {{ $t('commons.newVersion') }}
                    </app-button>
                </div>
            </div>
        </div>
        <ValidationObserver v-slot="{ dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-4" :label="$t('commons.infos')">
                    <app-input-text
                        :label="$t('commons.object')"
                        v-model="invoice.object"
                        class="col-span-2 md:col-span-1"
                        :required="true"
                        @blur="save()"
                        ref="objectInput"
                    />
                    <app-input-text
                        :label="$t('commons.code')"
                        v-model="invoice.code"
                        class="col-span-2 md:col-span-1"
                        @blur="save()"
                    />
                    <app-bundle-picker
                        class="col-span-2"
                        v-model="invoice.emitter"
                        :options="bundles"
                        @input="onBundleChange"
                    ></app-bundle-picker>
                    <app-number-input
                        :label="$t('commons.amountTTC')"
                        v-model="invoice.amountTTC"
                        :allowsNegative="true"
                        @blur="onAmountChange"
                        :currency="currencyFormat.currency"
                    />
                </app-fieldset>

                <app-fieldset class="col-span-2 mt-5" :label="$t('commons.dates')">
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="invoice.emissionDate"
                        :label="$t('invoices.emissionDate')"
                        @input="save()"
                    />
                    <app-date-input
                        class="col-span-2 md:col-span-1"
                        v-model="invoice.invoiceDate"
                        :label="$t('invoices.invoiceDate')"
                        @input="save()"
                    />
                </app-fieldset>
                <div class="col-span-2 mt-5">
                    <app-separator
                        :label="$t('invoices.attachment')"
                        icon="icon-file-document-outline"
                        class="my-5 col-span-4"
                    ></app-separator>
                    <div class="flex">
                        <app-upload-button
                            ref="fileInput"
                            icon="icon-paperclip"
                            :label="invoice.url ? $t('invoices.replaceDocument') : $t('invoices.newDocument')"
                            :end-point="`/api/projects/${$route.params.projectId}/invoices/${$route.params.invoiceId}/attachments`"
                            :name="$t('invoices.newDocument')"
                            class="text-sm offline:hidden"
                            @loaded="onLoaded"
                        />
                    </div>
                    <table class="w-full my-5" v-if="invoice.url">
                        <tr class="border-b hover:bg-gray-200">
                            <td class="p-2" style="width: 30px">
                                <a
                                    :href="invoice.url"
                                    :download="invoice.fileName"
                                    aria-label="download attachment"
                                    target="_blank"
                                >
                                    <IconDownload width="16" heigth="16" />
                                </a>
                            </td>
                            <td class="p-2 text-left">
                                <a :href="invoice.url" class="text-sm" aria-label="view attachment" target="_blank">
                                    {{ invoice.fileName }}
                                </a>
                            </td>
                            <td class="p-2" style="width: 30px" v-if="isAdmin">
                                <app-button
                                    size="mini"
                                    variant="danger"
                                    aria-label="remove attachment"
                                    icon="icon-trash-can-outline"
                                    @click="removeAttachment()"
                                />
                            </td>
                        </tr>
                    </table>
                </div>
                <app-fieldset class="col-span-2 mt-4" :label="$t('invoices.relatedQuotes')">
                    <table class="w-full col-span-2 text-xs">
                        <tr class="border-b">
                            <th class="p-2 border text-left">{{ $t('amendments.quote') }}</th>
                            <th class="p-2 border text-left">{{ $t('commons.type') }}</th>
                            <th class="p-2 border text-right">{{ $t('invoices.quoteAmountTTC') }}</th>
                            <th class="p-2 border text-right">{{ $t('invoices.priorRemainingAmountTTC') }}</th>
                            <th class="p-2 border text-right bg-blue-200">
                                <span :title="$t('invoices.subAmountTTCTips')">{{ $t('invoices.subAmountTTC') }}</span>
                            </th>
                            <th class="p-2 border text-right">{{ $t('invoices.remainingAmountTTC') }}</th>
                        </tr>
                        <tr class="border-b" v-for="amendment in chargedAmendments">
                            <td class="border p-2">
                                <router-link
                                    class="hover:underline"
                                    :to="{
                                        name: 'followAmendment',
                                        params: {
                                            ...$route.params,
                                            amendmentId: amendment.id,
                                        },
                                    }"
                                >
                                    <span class="text-xs">{{ amendment.code }}&nbsp;</span>
                                    <span>{{ amendment.object }}</span>
                                </router-link>
                            </td>
                            <td class="border p-2">
                                {{ amendment.type ? $t('invoices.amendmentSimplifiedTypes.' + amendment.type) : '' }}
                            </td>
                            <td class="border p-2 text-right">
                                <span>
                                    {{ amendment.amountTTC ? $n(amendment.amountTTC, currencyFormat) : '-' }}
                                </span>
                            </td>
                            <td class="border p-2">
                                <div class="flex justify-end gap-1 items-center">
                                    <span>
                                        {{
                                            amendment.remainingAmountTTC
                                                ? $n(amendment.remainingAmountTTC, currencyFormat)
                                                : '-'
                                        }}
                                    </span>
                                </div>
                            </td>
                            <td class="border p-2 text-right bg-blue-100">
                                <div
                                    v-if="
                                        amendment.quoteStatus !== 'validated' ||
                                        amendment.amendmentStatus !== 'signed' ||
                                        amendment.SOStatus !== 'signed'
                                    "
                                    :title="
                                        amendment.quoteStatus !== 'validated'
                                            ? $t('invoices.invoiceReadyNotValidatedQuote')
                                            : amendment.amendmentStatus !== 'signed'
                                            ? $t('invoices.invoiceReadyNotSignedAmendment')
                                            : amendment.SOStatus !== 'signed'
                                            ? $t('invoices.invoiceReadyNotSignedSO')
                                            : ''
                                    "
                                    class="text-red-500 w-full flex justify-center"
                                >
                                    <icon-alert-outline width="16px" height="16px" />
                                </div>
                                <div v-else class="flex justify-between items-center">
                                    <app-button
                                        v-if="amendment.remainingAmountTTC > 0"
                                        size="mini"
                                        variant="borderless"
                                        icon="icon-arrow-right-bold-box"
                                        class="opacity-10 hover:opacity-100"
                                        :title="$t('invoices.copyRemaining')"
                                        @click="copyRemainingAmountToChargedAmount(amendment)"
                                    ></app-button>
                                    <app-number-link
                                        :show-label="false"
                                        :label="$t('invoices.subAmountTTC')"
                                        v-model="amendment.chargedAmountTTC"
                                        :positiveOnly="false"
                                        format="decimal"
                                        @input="onAmendmentAmountChanged(amendment)"
                                        :currency="currencyFormat.currency"
                                        :disabled="
                                            amendment.quoteStatus !== 'validated' ||
                                            amendment.amendmentStatus !== 'signed' ||
                                            amendment.SOStatus !== 'signed'
                                        "
                                    >
                                        <div class="flex gap-1">
                                            <u class="w-full text-right">
                                                {{ $n(amendment.chargedAmountTTC || 0, currencyFormat) }}
                                            </u>
                                            <span
                                                :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                                v-if="amendment.amountTTC && amendment.amountTTC > 0"
                                                :class="{
                                                    'text-red-500 font-bold':
                                                        amendment.chargedAmountTTC > amendment.amountTTC,
                                                }"
                                            >
                                                ({{
                                                    Math.round(
                                                        (amendment.chargedAmountTTC / amendment.amountTTC) * 100,
                                                    )
                                                }}%)
                                            </span>
                                        </div>
                                    </app-number-link>
                                </div>
                            </td>
                            <td class="border p-2 text-right">
                                <span>
                                    {{
                                        amendment.remainingAmountTTC && amendment.chargedAmountTTC
                                            ? $n(
                                                  amendment.remainingAmountTTC - amendment.chargedAmountTTC,
                                                  currencyFormat,
                                              )
                                            : '-'
                                    }}
                                </span>
                                <span
                                    :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                    v-if="
                                        amendment.amountTTC &&
                                        amendment.remainingAmountTTC &&
                                        amendment.chargedAmountTTC
                                    "
                                    :class="{
                                        'text-red-500 font-bold':
                                            amendment.remainingAmountTTC < amendment.chargedAmountTTC,
                                    }"
                                >
                                    ({{
                                        Math.round((amendment.chargedAmountTTC / amendment.remainingAmountTTC) * 100)
                                    }}%)
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2"></td>
                            <td class="border p-2 text-right">{{ $n(totalQuotes, currencyFormat) }}</td>
                            <td class="border p-2 text-right">
                                {{ $n(totalPriorRemainingAmountTTC, currencyFormat) }}
                            </td>
                            <td class="border p-2">
                                <div
                                    class="flex justify-center gap-1"
                                    :class="{
                                        'text-red-500 font-bold': totalChargedAmendments !== invoice.amountTTC,
                                    }"
                                    :title="
                                        totalChargedAmendments !== invoice.amountTTC
                                            ? $t('invoices.distributedAmountDoesNotMatchInvoiceAmount')
                                            : ''
                                    "
                                >
                                    <span>
                                        {{ $n(totalChargedAmendments, currencyFormat) }}
                                    </span>
                                </div>
                            </td>
                            <td class="border p-2 text-right">{{ $n(totalRemainingAmountTTC, currencyFormat) }}</td>
                        </tr>
                    </table>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.distribution')" v-if="invoice.emitter">
                    <table class="w-full border text-xs col-span-2">
                        <tr class="border-b">
                            <th class="p-2 text-left">{{ $t('invoices.company') }}</th>
                            <th class="p-2 text-right bg-blue-200">{{ $t('commons.amountTTC') }}</th>
                        </tr>
                        <tr class="border-b">
                            <td class="border p-2" style="width: 70%">
                                <span>
                                    <b>[{{ $t('invoices.mainCompany') }}]</b>
                                    {{ invoice.emitter?.company?.name }}
                                </span>
                            </td>
                            <td class="border p-2 text-right bg-blue-200">
                                {{
                                    $n(
                                        invoice.distribution?.find((line) => line.id === invoice.emitterId)
                                            ?.amountTTC || 0,
                                        currencyFormat,
                                    )
                                }}
                            </td>
                        </tr>
                        <tr class="border-b" v-for="subcontractor in subcontractors">
                            <td class="border p-2">
                                <span>{{ subcontractor.name }}</span>
                            </td>
                            <td class="border p-2 text-right bg-blue-200">
                                <app-number-link
                                    :label="$t('commons.amountTTC')"
                                    :show-label="false"
                                    v-model="subcontractor.amountTTC"
                                    @input="saveDistribution()"
                                    :positiveOnly="false"
                                    format="decimal"
                                    class="underline"
                                >
                                    <span class="w-full text-right">
                                        {{ $n(subcontractor.amountTTC, currencyFormat) }}
                                    </span>
                                </app-number-link>
                            </td>
                        </tr>
                    </table>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.reviews')">
                    <div class="flex flex-col w-full col-span-2 gap-2">
                        <div>
                            <app-button
                                :label="$t('invoices.reviews')"
                                @click="$refs.multiBundlePicker.open()"
                            ></app-button>
                        </div>
                        <table class="w-full border text-xs" v-if="invoiceReviews.length > 0">
                            <tr class="border-b">
                                <th class="border p-2 text-left">{{ $t('invoices.reviews') }}</th>
                                <th class="border p-2 text-center">{{ $t('commons.emissionDueDate') }}</th>
                                <th class="border p-2 text-center">{{ $t('invoices.emissionDate') }}</th>
                                <th class="border p-2 text-right">{{ $t('invoices.conclusion') }}</th>
                                <th class="border p-2 text-right">{{ $t('invoices.comment') }}</th>
                            </tr>
                            <tr class="border-b" v-for="invoiceReview in invoiceReviews">
                                <td class="border p-2">
                                    <span>{{ invoiceReview.emitter.label }}</span>
                                </td>
                                <td class="border p-2">
                                    <div class="flex justify-center">
                                        <app-date-link
                                            :label="$t('commons.emissionDueDate')"
                                            :show-label="false"
                                            v-model="invoiceReview.emissionDueDate"
                                            @input="saveReview(invoiceReview)"
                                            class="underline"
                                            :disabled="!isAdmin"
                                        />
                                    </div>
                                </td>
                                <td class="border p-2">
                                    <div class="flex justify-center">
                                        <app-date-link
                                            :label="$t('invoices.emissionDate')"
                                            :show-label="false"
                                            v-model="invoiceReview.emissionDate"
                                            @input="saveReview(invoiceReview)"
                                            class="underline"
                                            :disabled="
                                                !isAdmin && !project.me.bundleIds.includes(invoiceReview.emitterId)
                                            "
                                        />
                                    </div>
                                </td>
                                <td class="border p-2 text-right">
                                    <app-select
                                        v-model="invoiceReview.conclusion"
                                        :label="$t('invoices.conclusion')"
                                        @input="saveReview(invoiceReview)"
                                        :show-label="false"
                                        :disabled="!isAdmin && !project.me.bundleIds.includes(invoiceReview.emitterId)"
                                    >
                                        <option value=""></option>
                                        <option value="validated">{{ $t('invoices.status.validated') }}</option>
                                        <option value="rejected">{{ $t('invoices.status.rejected') }}</option>
                                    </app-select>
                                </td>
                                <td class="border p-2 text-right">
                                    <app-textarea
                                        v-model="invoiceReview.comment"
                                        @blur="saveReview(invoiceReview)"
                                        :disabled="!isAdmin && !project.me.bundleIds.includes(invoiceReview.emitterId)"
                                    ></app-textarea>
                                </td>
                            </tr>
                        </table>
                    </div>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.conclusionMOE')">
                    <app-select
                        v-model="invoice.conclusion"
                        :label="$t('invoices.conclusion')"
                        @input="save()"
                        :disabled="!isAdmin"
                    >
                        <option value=""></option>
                        <option value="validated">{{ $t('invoices.status.validated') }}</option>
                        <option value="validatedWithComments">{{ $t('invoices.status.validatedWithComments') }}</option>
                        <option value="rejected">{{ $t('invoices.status.rejected') }}</option>
                        <option value="obsolete">{{ $t('invoices.status.obsolete') }}</option>
                    </app-select>
                    <app-date-input
                        v-model="invoice.conclusionDate"
                        :label="$t('invoices.conclusionDate')"
                        @input="save()"
                        :disabled="!isAdmin"
                    ></app-date-input>
                    <app-label
                        :label="$t('invoices.invoiceComment')"
                        v-if="
                            invoice.conclusion === 'rejected' ||
                            invoice.conclusion === 'obsolete' ||
                            invoice.conclusion === 'validatedWithComments'
                        "
                        :disabled="!isAdmin"
                    >
                        <app-textarea v-model="invoice.invoiceComment" @blur="save()"></app-textarea>
                    </app-label>
                </app-fieldset>

                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.situation')">
                    <div class="col-span-2 gap-2 flex flex-col">
                        <table class="w-full text-xs">
                            <tr class="border-b">
                                <th class="p-2 border text-left">{{ $t('invoices.deduction') }}</th>
                                <th class="p-2 border text-right">{{ $t('commons.amountTTC') }}</th>
                                <th class="p-2 border text-right">{{ $t('invoices.priorDeductionAmountTTC') }}</th>
                                <th class="p-2 border text-right bg-blue-200">
                                    <span :title="$t('invoices.deductionAmountTTCTips')">
                                        {{ $t('invoices.deductionAmountTTC') }}
                                    </span>
                                </th>
                                <th class="p-2 border text-right">
                                    {{ $t('invoices.remainingDeductionAmountTTC') }}
                                </th>
                            </tr>
                            <tr class="border-b" v-for="deduction in chargedDeductions">
                                <td class="border p-2">
                                    <span>{{ deduction.object }}</span>
                                </td>
                                <td class="border p-2 text-right">
                                    <span>{{ $n(deduction.amountTTC, currencyFormat) }}</span>
                                </td>
                                <td
                                    class="border p-2"
                                    :rowspan="deduction.id === 'definitivePenalties' ? 2 : 1"
                                    v-if="deduction.id !== 'provisionalPenalties'"
                                >
                                    <div class="flex justify-end gap-1 items-center">
                                        <span>
                                            {{ $n(deduction.remainingAmountTTC, currencyFormat) }}
                                        </span>
                                    </div>
                                </td>
                                <td class="border p-2 text-right bg-blue-100">
                                    <div class="flex justify-between items-center">
                                        <app-button
                                            v-if="deduction.remainingAmountTTC > 0"
                                            size="mini"
                                            variant="borderless"
                                            icon="icon-arrow-right-bold-box"
                                            class="opacity-10 hover:opacity-100"
                                            :title="$t('invoices.copyRemaining')"
                                            @click="copyRemainingAmountToChargedDeductionAmount(deduction)"
                                        ></app-button>
                                        <span v-else></span>
                                        <app-number-link
                                            :show-label="false"
                                            :label="$t('invoices.subAmountTTC')"
                                            v-model="deduction.chargedAmountTTC"
                                            :positiveOnly="false"
                                            format="decimal"
                                            @input="onDeductionAmountChanged(deduction)"
                                            :currency="currencyFormat.currency"
                                        >
                                            <div class="flex gap-1">
                                                <u class="w-full text-right">
                                                    {{ $n(deduction.chargedAmountTTC || 0, currencyFormat) }}
                                                </u>
                                                <span
                                                    :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                                    v-if="deduction.amountTTC && deduction.amountTTC > 0"
                                                    :class="{
                                                        'text-red-500 font-bold':
                                                            deduction.chargedAmountTTC > deduction.amountTTC,
                                                    }"
                                                >
                                                    ({{
                                                        Math.round(
                                                            (deduction.chargedAmountTTC / deduction.amountTTC) * 100,
                                                        )
                                                    }}%)
                                                </span>
                                            </div>
                                        </app-number-link>
                                    </div>
                                </td>
                                <td class="border p-2 text-right">
                                    <span>
                                        {{
                                            deduction.remainingAmountTTC && deduction.chargedAmountTTC
                                                ? $n(
                                                      deduction.remainingAmountTTC - deduction.chargedAmountTTC,
                                                      currencyFormat,
                                                  )
                                                : '-'
                                        }}
                                    </span>
                                    <span
                                        :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                        v-if="
                                            deduction.amountTTC &&
                                            deduction.remainingAmountTTC &&
                                            deduction.chargedAmountTTC
                                        "
                                        :class="{
                                            'text-red-500 font-bold':
                                                deduction.remainingAmountTTC < deduction.chargedAmountTTC,
                                        }"
                                    >
                                        ({{
                                            Math.round(
                                                (deduction.chargedAmountTTC / deduction.remainingAmountTTC) * 100,
                                            )
                                        }}%)
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td></td>
                                <td class="border p-2 text-right">{{ $n(totalDeductions, currencyFormat) }}</td>
                                <td class="border p-2 text-right">
                                    {{ $n(totalDeductionPriorRemainingAmountTTC, currencyFormat) }}
                                </td>
                                <td class="border p-2 text-right">
                                    {{ $n(totalChargedDeductions, currencyFormat) }}
                                </td>
                                <td class="border p-2 text-right">
                                    {{ $n(totalDeductionRemainingAmountTTC, currencyFormat) }}
                                </td>
                            </tr>
                        </table>
                        <table class="w-full border text-xs col-span-2">
                            <tr class="border-b">
                                <th class="p-2 text-left">{{ $t('invoices.toPay') }}</th>
                                <th class="p-2 text-right bg-blue-200">{{ $t('commons.amountTTC') }}</th>
                            </tr>
                            <tr class="border-b">
                                <td class="border p-2" style="width: 70%">
                                    <span>
                                        <b>[{{ $t('invoices.mainCompany') }}]</b>
                                        {{ invoice.emitter?.company?.name }}
                                    </span>
                                </td>
                                <td class="border p-2 text-right bg-blue-200">
                                    {{
                                        $n(
                                            (invoice.distribution?.find((line) => line.id === invoice.emitterId)
                                                ?.amountTTC || 0) - totalChargedDeductions,
                                            currencyFormat,
                                        )
                                    }}
                                </td>
                            </tr>
                            <tr class="border-b" v-for="subcontractor in subcontractors">
                                <td class="border p-2">
                                    <span>{{ subcontractor.name }}</span>
                                </td>
                                <td class="border p-2 text-right bg-blue-200">
                                    <app-number-link
                                        :label="$t('commons.amountTTC')"
                                        :show-label="false"
                                        v-model="subcontractor.situationAmountTTC"
                                        @input="saveSituationDistribution()"
                                        :positiveOnly="false"
                                        format="decimal"
                                        class="underline"
                                    >
                                        <span class="w-full text-right">
                                            {{ $n(subcontractor.situationAmountTTC, currencyFormat) }}
                                        </span>
                                    </app-number-link>
                                </td>
                            </tr>
                        </table>
                        <app-separator
                            :label="$t('invoices.situationAttachment')"
                            icon="icon-file-document-outline"
                            class="my-5 col-span-4"
                            variant="underline"
                        ></app-separator>
                        <div class="flex flex-col gap-2">
                            <app-upload-button
                                ref="situationFileInput"
                                icon="icon-paperclip"
                                :label="
                                    invoice.situationUrl ? $t('invoices.replaceSituation') : $t('invoices.newSituation')
                                "
                                :end-point="`/api/projects/${$route.params.projectId}/invoices/${$route.params.invoiceId}/situationAttachments`"
                                :name="$t('invoices.newSituation')"
                                class="text-sm offline:hidden"
                                @loaded="onSituationLoaded"
                            />
                            <table class="w-full my-5" v-if="invoice.situationUrl">
                                <tr class="border-b hover:bg-gray-200">
                                    <td class="p-2" style="width: 30px">
                                        <a
                                            :href="invoice.situationUrl"
                                            :download="invoice.fileName"
                                            aria-label="download attachment"
                                            target="_blank"
                                        >
                                            <IconDownload width="16" heigth="16" />
                                        </a>
                                    </td>
                                    <td class="p-2 text-left">
                                        <a
                                            :href="invoice.situationUrl"
                                            class="text-sm"
                                            aria-label="view attachment"
                                            target="_blank"
                                        >
                                            {{ invoice.situationFileName }}
                                        </a>
                                    </td>
                                    <td class="p-2" style="width: 30px" v-if="isAdmin">
                                        <app-button
                                            size="mini"
                                            variant="danger"
                                            aria-label="remove attachment"
                                            icon="icon-trash-can-outline"
                                            @click="removeSituationAttachment()"
                                        />
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </app-fieldset>
                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>
            </form>
        </ValidationObserver>
        <app-multi-picker
            ref="multiBundlePicker"
            class="col-span-2"
            variant="hidden"
            :show-label="false"
            v-model="selectedBundles"
            :options="bundles"
            :title="$t('invoices.chooseReviewers')"
            @close="validateReviewers"
        >
            <template v-slot:option="{ option }">
                <app-bundle :bundle="option"></app-bundle>
            </template>
            <template v-slot:footer>
                <div class="p-4 flex justify-end">
                    <app-button
                        @click="$refs.multiBundlePicker.close()"
                        variant="primary"
                        :label="$t('commons.save')"
                    ></app-button>
                </div>
            </template>
        </app-multi-picker>
    </div>
</template>

<script>
import { getMapById, reduceSumOn, sanitize } from '@/services/sanitize.service';
import AppSelect from '../../components/appSelect/AppSelect';
import AppInputText from '../../components/appInputText/AppInputText';
import AppFieldset from '../../components/appFieldset/AppFieldset';
import AppFooter from '../../components/appFooter/AppFooter';
import AppErrors from '../../components/appErrors/AppErrors';
import AppDateInput from '../../components/appDateInput/AppDateInput';
import AppButton from '../../components/appButton/AppButton';
import { createInvoice, getInvoices, updateInvoice } from './invoices.service';
import AppBundlePicker from '../../components/appBundlePicker';
import AppSaveOnLeave from '@/components/AppSaveOnLeave';
import AppSeparator from '@/components/appSeparator/AppSeparator';
import AppBundle from '@/components/app-bundle/appBundle';
import { combineLatest } from 'rxjs';
import { fetchSubcontractors, getBundles } from '@/features/bundles/bundles.service';
import { isMobile } from '@/state/state';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import { getCompanies } from '@/features/companies/companies.service';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput.vue';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import IconChevronDown from '@/icons/IconChevronDown.vue';
import { queryProject } from '@/features/projects/projects.service';
import AppUploadButton from '@/components/app-uploadButton/AppUploadButton.vue';
import { confirm } from '@/features/dialogs/dialogs.service';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import AppDateLink from '@/components/appDateLink/AppDateLink.vue';
import {
    createInvoiceReviews,
    getInvoiceReviews,
    removeInvoiceReviews,
    updateInvoiceReview,
} from '@/features/invoices/invoiceReviews.service';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import { getAmendments } from '@/features/amendments/amendments.service';
import IconAlertOutline from '@/icons/IconAlertOutline.vue';
import IconCheckCircleOutline from '@/icons/IconCheckCircleOutline.vue';
import { sortBy } from '@/services/sanitize.service';
import { getDeductions } from '@/features/deductions/deductions.service';
import { getCIEs } from '@/features/cie/CIE.service';

export default {
    components: {
        IconCheckCircleOutline,
        IconAlertOutline,
        AppMultiPicker,
        AppDateLink,
        AppNumberLink,
        AppUploadButton,
        IconChevronDown,
        AppTextarea,
        AppNumberInput,
        AppLabel,
        AppBundle,
        AppSeparator,
        AppSaveOnLeave,
        AppBundlePicker,
        AppButton,
        AppDateInput,
        AppErrors,
        AppFooter,
        AppFieldset,
        AppInputText,
        AppSelect,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
            this.isAdmin = project.me.allowedFeatures.includes('project_invoices');
            this.project = project;
        });
        this.init();
        localStorage.setItem(
            'invoice.lastVisitedInvoiceId.' + this.$route.params.projectId,
            this.$route.params.invoiceId,
        );
    },
    watch: {
        $route: function (from, to) {
            if (from.name !== to.name || from.params.invoiceId !== to.params.invoiceId) {
                this.focusDone = false;
            }
            this.init();
        },
    },
    computed: {
        totalChargedAmendments() {
            return reduceSumOn(this.chargedAmendments, 'chargedAmountTTC');
        },
        totalChargedDeductions() {
            return reduceSumOn(this.chargedDeductions, 'chargedAmountTTC');
        },
        totalQuotes() {
            return reduceSumOn(this.chargedAmendments, 'amountTTC');
        },
        totalPriorRemainingAmountTTC() {
            return reduceSumOn(this.chargedAmendments, 'remainingAmountTTC');
        },
        totalRemainingAmountTTC() {
            return reduceSumOn(this.chargedAmendments, (item) =>
                item.remainingAmountTTC && item.chargedAmountTTC ? item.remainingAmountTTC - item.chargedAmountTTC : 0,
            );
        },
        totalDeductions() {
            return reduceSumOn(this.chargedDeductions, 'amountTTC');
        },
        totalDeductionPriorRemainingAmountTTC() {
            return reduceSumOn(this.chargedDeductions, 'remainingAmountTTC');
        },
        totalDeductionRemainingAmountTTC() {
            return reduceSumOn(this.chargedDeductions, (item) =>
                item.remainingAmountTTC && item.chargedAmountTTC ? item.remainingAmountTTC - item.chargedAmountTTC : 0,
            );
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getInvoices(this.$route.params.projectId),
                    getInvoiceReviews(this.$route.params.projectId, this.$route.params.invoiceId),
                    getAmendments(this.$route.params.projectId),
                    getDeductions(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                ]).subscribe(async ([bundles, companies, invoices, invoiceReviews, amendments, deductions, cie]) => {
                    const relevantDeductions = deductions.filter(
                        (deduction) => deduction.deductionStatus && deduction.deductionStatus !== 'obsolete',
                    );
                    const relevantCie = cie.filter((cie) => cie.quoteStatus && cie.quoteStatus !== 'validated');
                    const companyMap = getMapById(companies);
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                    }));
                    this.bundleMap = getMapById(mappedBundles);
                    const invoice = invoices.find((invoice) => invoice.id === this.$route.params.invoiceId);
                    const amountTTCAlreadyChargedByAmendmentId = invoices
                        .filter(
                            (anInvoice) =>
                                anInvoice.conclusion === 'validated' &&
                                anInvoice.id !== invoice.id &&
                                anInvoice.situationEmissionDate &&
                                anInvoice.situationEmissionDate.getTime() <
                                    (invoice.invoiceDate || invoice.emissionDate || invoice.createdAt).getTime(),
                        )
                        .reduce((acc, invoice) => {
                            if (invoice.distributionByAmendments) {
                                return [...acc, ...invoice.distributionByAmendments];
                            } else {
                                return acc;
                            }
                        }, [])
                        .reduce((acc, distribution) => {
                            if (acc[distribution.id]) {
                                acc[distribution.id] += distribution.amountTTC;
                            } else {
                                acc[distribution.id] = distribution.amountTTC;
                            }
                            return acc;
                        }, {});
                    const amountTTCAlreadyDeductedByAmendmentId = invoices
                        .filter(
                            (anInvoice) =>
                                anInvoice.conclusion === 'validated' &&
                                anInvoice.id !== invoice.id &&
                                invoice.emitterId === invoice.id,
                        )
                        .reduce((acc, invoice) => {
                            if (invoice.chargedDeductions) {
                                return [...acc, ...invoice.chargedDeductions];
                            } else {
                                return acc;
                            }
                        }, [])
                        .reduce((acc, distribution) => {
                            if (acc[distribution.id]) {
                                acc[distribution.id] += distribution.chargedAmountTTC;
                            } else {
                                acc[distribution.id] = distribution.chargedAmountTTC;
                            }
                            return acc;
                        }, {});
                    this.invoice = {
                        distributionByAmendments: [],
                        chargedDeductions: [],
                        ...invoice,
                        emitter: this.bundleMap[invoice.emitterId],
                    };
                    this.bundles = mappedBundles;
                    this.invoiceReviews = invoiceReviews.map((invoiceReview) => ({
                        ...invoiceReview,
                        emitter: this.bundleMap[invoiceReview.emitterId],
                    }));
                    this.selectedVersion = this.$route.params.invoiceId;
                    await this.refreshSubcontractors();

                    this.selectedBundles = this.selectedBundles = this.invoiceReviews
                        .map((invoiceReview) => invoiceReview.emitter)
                        .filter((a) => !!a);

                    if (!this.focusDone && !isMobile && this.invoice.name === '') {
                        this.$nextTick(() => {
                            this.$refs.objectInput.focus();
                        });
                        this.focusDone = true;
                    }
                    this.amendments = sortBy(
                        amendments.filter(
                            (amendment) => amendment.quoteStatus !== 'rejected' && amendment.quoteStatus !== 'obsolete',
                        ),
                        (amendment) => (amendment.code || 'zzz') + amendment.object,
                    );
                    const cieDeduction = relevantCie
                        .map((cie) => ({
                            ...cie,
                            amountTTC:
                                cie.distribution.find((distribution) => distribution.bundleId === invoice.emitterId)
                                    ?.amount || 0,
                        }))
                        .reduce(
                            (acc, cie) => {
                                return { ...acc, amountTTC: (acc.amountTTC += cie.amountTTC) };
                            },
                            { id: 'cie', amountTTC: 0, type: 'cie', object: this.$t('cie.engagedCIE') },
                        );
                    cieDeduction.remainingAmountTTC =
                        cieDeduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId['cie'] || 0);
                    const garanteeDeduction = relevantDeductions
                        .filter(
                            (deduction) =>
                                deduction.type === 'garanteeDeduction' && deduction.bundleId === invoice.emitterId,
                        )
                        .reduce(
                            (acc, deduction) => {
                                return { ...acc, amountTTC: (acc.amountTTC += deduction.amountTTC) };
                            },
                            {
                                id: 'garanteeDeduction',
                                amountTTC: 0,
                                type: 'garanteeDeduction',
                                object: this.$t('deductions.types.garanteeDeduction'),
                            },
                        );
                    garanteeDeduction.remainingAmountTTC =
                        garanteeDeduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId['garanteeDeduction'] || 0);
                    const provisionalPenaltyDeduction = deductions
                        .filter(
                            (deduction) =>
                                deduction.type === 'penalty' &&
                                deduction.bundleId === invoice.emitterId &&
                                deduction.deductionStatus === 'provisional',
                        )
                        .reduce(
                            (acc, deduction) => {
                                return { ...acc, amountTTC: (acc.amountTTC += deduction.amountTTC) };
                            },
                            {
                                id: 'provisionalPenalties',
                                amountTTC: 0,
                                type: 'penalty',
                                object: this.$t('invoices.provisionalPenalties'),
                            },
                        );
                    const definitivePenaltyDeduction = deductions
                        .filter(
                            (deduction) =>
                                deduction.type === 'penalty' &&
                                deduction.bundleId === invoice.emitterId &&
                                deduction.deductionStatus === 'definitive',
                        )
                        .reduce(
                            (acc, deduction) => {
                                return { ...acc, amountTTC: (acc.amountTTC += deduction.amountTTC) };
                            },
                            {
                                id: 'definitivePenalties',
                                amountTTC: 0,
                                type: 'penalty',
                                object: this.$t('invoices.definitivePenalties'),
                            },
                        );
                    definitivePenaltyDeduction.remainingAmountTTC =
                        definitivePenaltyDeduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId['penalty'] || 0);
                    const otherDeductions = deductions
                        .filter(
                            (deduction) => deduction.type === 'deduction' && deduction.bundleId === invoice.emitterId,
                        )
                        .map((deduction) => ({
                            ...deduction,
                            remainingAmountTTC:
                                deduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId[deduction.id] || 0),
                        }));
                    this.deductions = [
                        ...otherDeductions,
                        garanteeDeduction,
                        cieDeduction,
                        definitivePenaltyDeduction,
                        provisionalPenaltyDeduction,
                    ];
                    this.chargedAmendments = this.amendments
                        .filter((amendment) => amendment.bundleId === invoice.emitterId)
                        .map((amendment) => ({
                            ...amendment,
                            remainingAmountTTC:
                                amendment.amountTTC - (amountTTCAlreadyChargedByAmendmentId[amendment.id] || 0),
                            chargedAmountTTC:
                                this.invoice.distributionByAmendments.find(
                                    (chargedAmendment) => chargedAmendment.id === amendment.id,
                                )?.amountTTC || 0,
                        }));
                    this.chargedDeductions = this.deductions
                        .filter((deduction) => deduction.deductionStatus !== 'obsolete')
                        .map((deduction) => ({
                            ...deduction,
                            remainingAmountTTC:
                                deduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId[deduction.id] || 0),
                            chargedAmountTTC:
                                this.invoice.chargedDeductions.find(
                                    (chargedDeduction) => chargedDeduction.id === deduction.id,
                                )?.amountTTC || 0,
                        }));
                }),
            ];
        },
        async validateReviewers() {
            const bundlesToAdd = this.selectedBundles.filter(
                (bundle) => !this.invoiceReviews.find((invoiceReview) => invoiceReview.emitterId === bundle.id),
            );
            const reviewersToRemove = this.invoiceReviews.filter(
                (invoiceReview) => !this.selectedBundles.find((bundle) => invoiceReview.emitterId === bundle.id),
            );
            await Promise.all(
                bundlesToAdd.map((bundle) =>
                    createInvoiceReviews(this.$route.params.projectId, this.invoice.id, { emitterId: bundle.id }),
                ),
            );
            await Promise.all(
                reviewersToRemove.map((invoiceReview) =>
                    removeInvoiceReviews(this.$route.params.projectId, invoiceReview.id),
                ),
            );
        },
        computeNewDistribution() {
            const subcontractorMap = getMapById(this.subcontractors);
            const cleanDistribution = this.subcontractors.filter(
                (line) =>
                    subcontractorMap[line.id] &&
                    (subcontractorMap[line.id].amountTTC > 0 || subcontractorMap[line.id].amountHT > 0),
            );
            const totalDistributionTTC = cleanDistribution.reduce((acc, line) => acc + line.amountTTC, 0);
            return [
                {
                    id: this.invoice.emitterId,
                    name: this.invoice.emitter?.company?.name,
                    amountTTC: this.invoice.amountTTC - totalDistributionTTC,
                },
                ...cleanDistribution,
            ];
        },
        async saveDistribution() {
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                distribution: this.computeNewDistribution(),
            });
        },
        computeNewSituationDistribution() {
            const subcontractorMap = getMapById(this.subcontractors);
            const cleanDistribution = this.subcontractors.filter(
                (line) => subcontractorMap[line.id] && subcontractorMap[line.id].situationAmountTTC !== 0,
            );

            const emitterDistributionAmountTTC =
                (this.invoice.distribution.find((line) => line.id === this.invoice.emitterId)?.amountTTC || 0) -
                this.totalChargedDeductions;

            return [
                {
                    id: this.invoice.emitterId,
                    name: this.invoice.emitter?.company?.name,
                    amountTTC: emitterDistributionAmountTTC,
                },
                ...cleanDistribution.map((distribution) => ({
                    id: distribution.id,
                    amountTTC: distribution.situationAmountTTC,
                })),
            ];
        },
        async saveSituationDistribution() {
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                situationDistribution: this.computeNewSituationDistribution(),
            });
        },
        copyRemainingAmountToChargedAmount(amendment) {
            amendment.chargedAmountTTC = amendment.remainingAmountTTC;
            this.onAmendmentAmountChanged();
        },
        copyRemainingAmountToChargedDeductionAmount(deduction) {
            deduction.chargedAmountTTC = deduction.remainingAmountTTC;
            this.onDeductionAmountChanged();
        },
        onAmendmentAmountChanged() {
            this.saveDistribution();
            this.saveSituationDistribution();
            updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                distributionByAmendments: this.chargedAmendments
                    .map((chargedAmendment) => ({
                        id: chargedAmendment.id,
                        amountTTC: chargedAmendment.chargedAmountTTC,
                    }))
                    .filter((chargedAmendment) => chargedAmendment.amountTTC !== 0),
            });
        },
        onDeductionAmountChanged() {
            updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                chargedDeductions: this.chargedDeductions
                    .map((chargedDeduction) => ({
                        id:
                            chargedDeduction.type === 'cie'
                                ? 'cie'
                                : chargedDeduction.type === 'penalty'
                                ? 'penalty'
                                : chargedDeduction.id,
                        amountTTC: chargedDeduction.chargedAmountTTC,
                    }))
                    .filter((chargedDeduction) => chargedDeduction.amountTTC !== 0),
            });
        },
        async refreshSubcontractors() {
            if (this.invoice?.emitterId) {
                const distributionMap = getMapById(this.invoice.distribution);
                const situationDistributionMap = getMapById(this.invoice.situationDistribution);
                this.subcontractors = (
                    await fetchSubcontractors(this.$route.params.projectId, this.invoice.emitterId)
                ).map((subcontractor) => ({
                    ...subcontractor,
                    amountTTC: distributionMap[subcontractor.id]?.amountTTC || 0,
                    situationAmountTTC: situationDistributionMap[subcontractor.id]?.amountTTC || 0,
                }));
            } else {
                this.subcontractors = [];
            }
        },
        onBundleChange() {
            this.invoice.distribution = [];
            this.refreshSubcontractors();
            return updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        onAmountChange() {
            return updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                situationDistribution: this.computeNewSituationDistribution(),
                distribution: this.computeNewDistribution(),
            });
        },
        async onVersionSelected(invoiceId) {
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    invoiceId,
                },
            });
        },
        async onNewVersion() {
            const invoiceEntity = {
                groupId: this.invoice.groupId,
                object: this.invoice.object,
                code: this.invoice.code,
                emissionDate: null,
                emitterId: this.invoice.emitterId,
            };
            const result = await createInvoice(this.$route.params.projectId, invoiceEntity);
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    invoiceId: result.id,
                },
            });
        },
        sanitizeInvoice(invoice) {
            return {
                ...sanitize(invoice, ['emitter', 'versions']),
                emitterId: invoice.emitter ? invoice.emitter.id : null,
            };
        },
        async save() {
            if (this.savingFlag) {
                return;
            } else {
                this.savingFlag = true;
            }
            try {
                const invoiceEntity = this.sanitizeInvoice(this.invoice);
                await updateInvoice(this.$route.params.projectId, {
                    id: this.invoice.id,
                    ...invoiceEntity,
                });
                if (this.$refs.observer) {
                    this.$refs.observer.reset();
                }
            } finally {
                this.savingFlag = false;
            }
        },
        async saveReview(invoiceReview) {
            await updateInvoiceReview(this.$route.params.projectId, {
                id: invoiceReview.id,
                comment: invoiceReview.comment,
                emissionDate: invoiceReview.emissionDate,
                conclusion: invoiceReview.conclusion,
                emissionDueDate: invoiceReview.emissionDueDate,
            });
        },
        async createNew() {
            const result = await createInvoice(this.$route.params.projectId, {
                name: '',
            });
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    invoiceId: result.id,
                },
            });
        },
        onLoaded(attachment) {
            this.invoice.url = attachment.url;
            this.invoice.fileName = attachment.fileName;
        },
        onSituationLoaded(attachment) {
            this.invoice.situationUrl = attachment.url;
            this.invoice.situationFileName = attachment.fileName;
        },
        async removeAttachment() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                this.invoice.url = null;
                this.invoice.fileName = null;
                await this.save();
            }
        },
        async removeSituationAttachment() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                this.invoice.situationUrl = null;
                this.invoice.situationFileName = null;
                await this.save();
            }
        },
    },
    data() {
        return {
            amendments: [],
            deductions: [],
            chargedAmendments: [],
            chargedDeductions: [],
            selectedBundles: [],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            invoiceReviews: [],
            focusDone: false,
            subcontractors: [],
            subscriptions: [],
            bundleMap: {},
            savingFlag: false,
            bundles: [],
            invoice: {
                name: '',
                emitter: null,
                versions: [],
                distributionByAmendments: [],
                chargedDeductions: [],
            },
            selectedVersion: null,
            isAdmin: false,
            project: {},
        };
    },
};
</script>
